




































































































































/* eslint-disable @typescript-eslint/no-explicit-any */
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";

import ChangeAssociationModal from "./ChangeAssociationModal.vue";
import InvalidIDModal from "./InvalidIDModal.vue";
import VentilatorModal from "./VentilatorModal.vue";
import PatientErrorModal from "./PatientErrorModal.vue";
import ManualEntryModal from "./ManualEntryModal.vue";
import SearchBar, { EnteredData } from "./SearchBar.vue";

import Patient from "./patient/Patient.vue";
import Devices from "./devices/Devices.vue";

import { BaseRootPageComponent } from "@/components/BaseRootPageComponent";
import { CLEAR_ASSOCIATION_MESSAGE, FOCUS_SEARCHBAR } from "@/utils/constants";
import CoreAPI from "@/utils/core-api";
import EventBus from "@/utils/event-bus";
import * as modals from "@/utils/modals";
import { Device, PatientDetails, ScannedDevice, ScanResponse, ScanResponseType, Settings } from "@/models";
import { getPatientFullName } from "@/filters/patientName";

let workflowTimer: number;

@Component({
  components: {
    SearchBar,
    Patient,
    Devices,
    ChangeAssociationModal,
    InvalidIDModal,
    VentilatorModal,
    PatientErrorModal,
    ManualEntryModal
  }
})
export default class Associate extends BaseRootPageComponent {
  @Prop() readonly settings!: Settings;

  readonly scanResponseType = ScanResponseType;

  input: EnteredData | null = null;
  scannedEntity: any = {};
  patient: any = null;
  ventilator: any = null;
  tempDevice: any = null;
  tempPatientID: string | null = null;
  scanType: ScanResponseType | null = null;
  showManualEntryReason = false;
  manualEntryReasonCallback?: (event: { cancel: boolean }) => void;
  showChangeAssociation = false;
  showInvalidId = false;
  showVentilatorError = false;
  showPatientError = false;
  msg = "";
  changeAssociationModalTitle = "";
  changeAssociationModalBody = "";
  scanFirst: ScanResponseType | null = null;
  successMessage = "";

  get patientOrVentSelected() {
    return this.patient || this.ventilator;
  }

  get isAssociated() {
    return this.patient && this.ventilator;
  }

  get isMobile() {
    return this.$nativeApp.isMobile;
  }

  mounted() {
    EventBus.$emit(FOCUS_SEARCHBAR);
    EventBus.$on(CLEAR_ASSOCIATION_MESSAGE, this.clearSuccessMessage);
  }

  updated() {
    // clear last interval before setting new one
    clearInterval(workflowTimer);
    const timeout = this.settings.workflowTimeout * 1000 * 60;
    workflowTimer = window.setInterval(this.resetWorkflow, timeout);
  }

  beforeDestroy() {
    this.resetWorkflow();
    clearInterval(workflowTimer);
    EventBus.$off(CLEAR_ASSOCIATION_MESSAGE, this.clearSuccessMessage);
  }

  async scanBarcode() {
    this.clearSuccessMessage();

    // Prompt the user to scan the barcode
    const barcode = await this.$nativeApp.scanBarcode();

    // Send the barcode to the search bar component
    (this.$refs?.searchBar as any).scanBarcode(barcode);
  }

  async showManualEntryReasonModal() {
    return new Promise<boolean>((resolve, reject) => {
      // In the unlikely event that there's a pending callback, call it
      this.manualEntryReasonCallback?.({ cancel: true });

      // Setup the callback so that when the modal closes, this promise gets resolved
      this.manualEntryReasonCallback = event => {
        // Clear the callback after getting the result
        this.manualEntryReasonCallback = undefined;

        // Tell the modal to close
        this.showManualEntryReason = false;

        resolve(event.cancel);
      };

      // Now that the callback is setup, show the modal
      this.showManualEntryReason = true;
    });
  }

  async scan(entered: EnteredData) {
    this.clearSuccessMessage();

    let response: any;
    this.input = entered;
    try {
      response = await CoreAPI.scan(this.input.raw, this.input.isScanned);
    } catch (err) {
      const error = err as any;
      if (error.isNetworkError) {
        await modals.showLostConnectionModal(this.$bvModal);
      } else if (error.status == 404) {
        // If the user manually entered the barcode and a patient or device wasn't found, still ask them why they
        // didn't use a scanner (since the reason a match wasn't found could be they had a typo from the manual entry)
        if (!entered.isScanned) {
          await this.showManualEntryReasonModal();
        }

        await this.handleMissingDataError();
      } else {
        console.error("Failed scanning ventilator/patient", error);
        await modals.showUnexpectedErrorModal(this.$bvModal);
      }

      EventBus.$emit(FOCUS_SEARCHBAR);
      return;
    }

    // If the user manually entered a barcode (not a device name), ask them to provide the reason they didn't use a
    // scanner
    const needsManualReason = !entered.isScanned && !(this.input.raw === response.device?.name);
    if (needsManualReason) {
      const canceled = await this.showManualEntryReasonModal();
      if (canceled) {
        EventBus.$emit(FOCUS_SEARCHBAR);
        return;
      }
    }

    try {
      this.setScanFirstAndTypeValues(response);
      if (response?.type === ScanResponseType.DEVICE) {
        // Handle device response
        this.scannedEntity = response.device;
        await this.handleVentilatorResponse(response.device);
      } else if (response?.type === ScanResponseType.PATIENT) {
        this.scannedEntity = response.patient;
        // Handle patient response
        await this.handlePatientResponse(response.patient);
      } else {
        this.showInvalidIdModal();
      }
    } catch (err) {
      const error = err as any;
      if (error.isNetworkError) {
        await modals.showLostConnectionModal(this.$bvModal);
      } else if (error.status == 404) {
        await this.handleMissingDataError();
      } else {
        console.error("Failed scanning ventilator/patient", error);
        await modals.showUnexpectedErrorModal(this.$bvModal);
      }
    } finally {
      EventBus.$emit(FOCUS_SEARCHBAR);
    }
  }

  setScanFirstAndTypeValues(response: ScanResponse) {
    this.scanType = response?.type;
    if (!this.patient && !this.ventilator) this.scanFirst = this.scanType;
  }

  async handleMissingDataError() {
    if (this.patient === null && this.ventilator === null) {
      await this.$bvModal.msgBoxOk(
        `Could not find a patient or ventilator for the entered barcode: ${this.input?.raw}.\n\nPlease double check the barcode and try again.`,
        {
          title: "Patient/Ventilator Not Found",
          hideHeaderClose: false,
          noCloseOnBackdrop: true
        }
      );
    } else if (this.scanFirst === this.scanResponseType.PATIENT) {
      await this.$bvModal.msgBoxOk(
        `A ventilator could not be found for the scanned barcode (${this.input?.raw}).\n\nThis may be caused by the ventilator being unable to communicate with the system.\n\nPlease contact IT for assistance.`,
        {
          title: "Ventilator Not Found",
          hideHeaderClose: false,
          noCloseOnBackdrop: true
        }
      );
    } else if (this.scanFirst === this.scanResponseType.DEVICE) {
      await this.$bvModal.msgBoxOk(`A Patient could not be found for the entered barcode: ${this.input?.raw}.`, {
        title: "Patient Not Found",
        hideHeaderClose: false,
        noCloseOnBackdrop: true
      });
    }
  }

  clearSuccessMessage() {
    this.successMessage = "";
  }

  async handleVentilatorResponse(device: ScannedDevice) {
    try {
      /* Vent First flow  */
      // Show error if we scanned another/same vent instead of patient
      if (this.ventilator && device) {
        if (this.ventilator.serial !== device.serial && this.scanFirst === this.scanResponseType.DEVICE) {
          this.showVentilatorErrorModal();
          return;
        } else if (this.ventilator.serial === device.serial) {
          await this.$bvModal.msgBoxOk(`Ventilator scanned already.`, {
            title: "Entered the same ventilator",
            noCloseOnBackdrop: true
          });
          return;
        }
      }

      /* Patient First flow  */
      // If scanned vent is associated to Another Patient - Remove Association
      if (
        this.scanFirst === this.scanResponseType.PATIENT &&
        this.patient &&
        device.patientID &&
        device.patientID !== this.patient.id
      ) {
        await this.removeAssociationFlowForPatientFirst(device);
        return;
      }

      // If scanned vent is not associated to Another Patient - follow device disconnect flow
      await this.handleFlowForDeviceDisconnected(device);
    } catch (error) {
      const err = error as any;
      if (err.isNetworkError) {
        await modals.showLostConnectionModal(this.$bvModal);
      } else if (err.status === 409) {
        if (err.data.title === "PatientHasVentilator") {
          this.showVentilatorErrorModal();
        } else if (err.data.title === "DeviceHasPatient") {
          this.changeAssociationModalTitle = `Change Association`;
          this.changeAssociationModalBody = `The patient ${device.patientID} is currently associated with the ventilator ${device.serial}.`;
          this.showChangeAssociationModal();
        }
      } else {
        console.error("Failed scanning ventilator", error);
        await modals.showUnexpectedErrorModal(this.$bvModal);
      }
    }
  }

  async removeAssociationFlowForPatientFirst(device: ScannedDevice) {
    const tempPatient = await CoreAPI.getPatient(device.patientID as string);
    const proceed = await this.$bvModal.msgBoxConfirm(
      `The ventilator ${device.name ? device.name : device.serial} is already associated to ${getPatientFullName(
        tempPatient
      )}. Would you like to remove the association from ${getPatientFullName(
        tempPatient
      )} to associate it to ${getPatientFullName(this.patient)}?`,
      {
        title: `Ventilator Associated to Another Patient`,
        okTitle: `Remove Association`,
        cancelTitle: "Cancel",
        cancelVariant: "light",
        id: "vent_remove",
        noCloseOnBackdrop: true,
        hideHeaderClose: false
      }
    );

    if (proceed) {
      await this.removeAssociationCallback();
    }
  }

  async handleFlowForDeviceDisconnected(device: ScannedDevice) {
    // the device isn't currently connected to VVS, so warn the user
    if (!device.isConnected) {
      const proceed = await this.$bvModal.msgBoxConfirm(
        `Unable to connect to ventilator ${
          device.name ? device.name : device.serial
        }. The ventilator is either off, not in wireless range, or has a bad configuration.`,
        {
          title: "Ventilator Disconnected",
          okTitle: this.patientOrVentSelected ? "Associate Anyway" : "Continue",
          cancelTitle: "Cancel",
          cancelVariant: "light",
          id: "vent_disconnect",
          hideHeaderClose: false,
          noCloseOnBackdrop: true
        }
      );

      if (!proceed) return;
    }

    // Patient first - Patient already has a vent associated - show change association msg
    if (this.scanFirst === this.scanResponseType.PATIENT && this.ventilator) {
      this.tempDevice = this.scannedEntity;
      this.changeAssociationModalTitle = "Change Association";
      this.changeAssociationModalBody = `Would you like to change the ventilator for ${getPatientFullName(
        this.patient
      )} from ${this.ventilator.name ? this.ventilator.name : this.ventilator.serial} to ${
        this.tempDevice.name ? this.tempDevice.name : this.tempDevice.serial
      }?`;
      this.showChangeAssociationModal();
      return;
    }

    // Patient first - Patient does not have a vent associated - show Associate Ventilator confirmation
    if (this.patient !== null) {
      await CoreAPI.associateDevice(device.serial, this.patient.id);
    } else if (this.scanFirst === this.scanResponseType.DEVICE && device.patientID) {
      this.patient = await CoreAPI.getPatient(device.patientID);
    }

    this.addDevice(device);
    EventBus.$emit(FOCUS_SEARCHBAR);
  }

  async handlePatientResponse(patient: PatientDetails) {
    try {
      /* Patient First flow  */
      // Show error if we scanned another patient instead of vent
      if (patient && this.patient) {
        if (patient.id !== this.patient.id && this.scanFirst === this.scanResponseType.PATIENT) {
          this.showPatientErrorModal();
          return;
        } else if (patient.id === this.patient.id) {
          await this.$bvModal.msgBoxOk(`Patient scanned already.`, {
            title: "Entered the same patient",
            hideHeaderClose: false,
            noCloseOnBackdrop: true
          });
          return;
        }
      }

      /* Vent First flow  */
      // Scanned patient is already associated - remove association
      if (
        this.ventilator !== null &&
        patient.associatedDevices.length &&
        patient.associatedDevices[0].serial !== this.ventilator.serial
      ) {
        await this.removeAssociationFlowForVentFirst(patient);
        return;
      }

      // Scanned patient is not associated but current vent has an assoicated patient - change association
      else if (this.ventilator !== null && this.patient !== null && patient.associatedDevices.length === 0) {
        this.changeAssociationModalTitle = `Change Association`;
        this.changeAssociationModalBody = `Would you like to change the association for ${this.getVentName(
          this.ventilator
        )} from ${getPatientFullName(this.patient)} to ${getPatientFullName(patient)}?`;
        this.showChangeAssociationModal();
        return;
      }

      // Both scanned Patient and current vent are not associated
      if (this.ventilator !== null && patient) {
        await CoreAPI.associateDevice(this.ventilator.serial, patient.id);
      }

      // Patient First - scanned Patient is already associated
      if (patient.associatedDevices.length > 0) {
        this.addDevice(patient.associatedDevices[0]);
      }

      //Patient first and Vent first - add Patient and show association message
      if (this.patient === null) {
        this.addPatient(patient);
      }
    } catch (err) {
      const error = err as any;
      if (error.isNetworkError) {
        await modals.showLostConnectionModal(this.$bvModal);
      } else if (error.status === 404) {
        this.showInvalidIdModal();
      } else if (error.status === 409 && error.data.title === "PatientHasVentilator" && patient) {
        this.changeAssociationModalTitle = `Change Association`;
        this.changeAssociationModalBody = `The patient ${getPatientFullName(
          patient
        )} is currently associated with the ventilator ${this.getVentName(patient.associatedDevices[0])}.`;
        this.showChangeAssociationModal();
      } else {
        console.error("Failed scanning patient", error);
        await modals.showUnexpectedErrorModal(this.$bvModal);
      }
    }
  }

  async removeAssociationFlowForVentFirst(patient: PatientDetails) {
    this.tempDevice = patient.associatedDevices[0];
    const proceed = await this.$bvModal.msgBoxConfirm(
      `The patient ${getPatientFullName(patient)} is already associated to ${this.getVentName(
        this.tempDevice
      )}. Would you like to remove the association from ${this.getVentName(
        this.tempDevice
      )} to associate it to ${this.getVentName(this.ventilator)}?`,
      {
        title: `Patient Associated to Another Ventilator`,
        okTitle: `Remove Association`,
        cancelTitle: "Cancel",
        cancelVariant: "light",
        id: "vent_remove",
        noCloseOnBackdrop: true
      }
    );

    if (proceed) {
      await this.removeAssociationCallback();
    }
  }

  showChangeAssociationModal() {
    this.showChangeAssociation = true;
  }

  showVentilatorErrorModal() {
    this.showVentilatorError = true;
  }

  showPatientErrorModal() {
    this.showPatientError = true;
  }

  showInvalidIdModal() {
    this.showInvalidId = true;
  }

  getVentName(device: ScannedDevice | Device) {
    return device.name ? device.name : device.serial;
  }

  async removeAssociationCallback() {
    // disassociate
    try {
      if (this.scanFirst === this.scanResponseType.PATIENT) {
        await this.removeAssociation(this.scannedEntity); // disassociate scanned vent patient
        await this.handleFlowForDeviceDisconnected(this.scannedEntity);
      } else if (this.scanFirst === this.scanResponseType.DEVICE) {
        await this.removeAssociation(this.tempDevice);
        if (this.ventilator && this.patient) {
          // Existing vent already associated - Change Association
          this.changeAssociationModalTitle = `Change Association`;
          this.changeAssociationModalBody = `Would you like to change the association for ${this.getVentName(
            this.ventilator
          )} from ${getPatientFullName(this.patient)} to ${getPatientFullName(this.scannedEntity)}?`;
          this.showChangeAssociationModal();
        } else if (this.scannedEntity) {
          // Existing vent not associated, associate with scanned patient
          await CoreAPI.associateDevice(this.ventilator.serial, this.scannedEntity.id);
          this.addPatient(this.scannedEntity);
        }
      }
    } catch (error) {
      const err = error as any;
      if (err.isNetworkError) {
        await modals.showLostConnectionModal(this.$bvModal);
      } else {
        console.error("Failed changing association", error);
        await modals.showUnexpectedErrorModal(this.$bvModal);
      }
    }
  }

  async removeAssociation(device?: any) {
    const deviceToDisassociate = device ? device : this.scannedEntity;
    // disassociate
    try {
      await CoreAPI.associateDevice(deviceToDisassociate.serial, null);
      deviceToDisassociate.patientID = null;
    } catch (error) {
      const err = error as any;
      if (err.isNetworkError) {
        await modals.showLostConnectionModal(this.$bvModal);
      } else {
        console.error("Failed changing association", error);
        await modals.showUnexpectedErrorModal(this.$bvModal);
      }
    }
  }

  async changeAssociation() {
    try {
      // Patient first and Vent first - remove current vent association
      await this.removeAssociation(this.ventilator);
      // nothing to associate, just add vent
      if (this.patient === null && this.ventilator === null) {
        this.addDevice(this.tempDevice);
        return;
      }
      // associate scanned vent with patient
      if (this.patient !== null && this.scanFirst === this.scanResponseType.PATIENT) {
        await CoreAPI.associateDevice(this.tempDevice.serial, this.patient.id);
        this.addDevice(this.tempDevice);
      }
      // associate scanned patient with vent
      if (
        this.ventilator !== null &&
        this.scanFirst === this.scanResponseType.DEVICE &&
        this.scannedEntity &&
        this.scannedEntity.id
      ) {
        await CoreAPI.associateDevice(this.ventilator.serial, this.scannedEntity.id);
        this.addPatient(this.scannedEntity);
      }
    } catch (error) {
      const err = error as any;
      if (err.isNetworkError) {
        await modals.showLostConnectionModal(this.$bvModal);
      } else {
        console.error("Failed changing association", error);
        await modals.showUnexpectedErrorModal(this.$bvModal);
      }
    } finally {
      EventBus.$emit(FOCUS_SEARCHBAR);
    }
  }

  addPatient(patient: any) {
    this.patient = patient;
    this.successMessage = this.scanFirst === ScanResponseType.PATIENT ? "Patient selected" : "Association completed";
  }

  addDevice(device: any) {
    this.ventilator = device;
    this.successMessage = this.scanFirst === ScanResponseType.DEVICE ? "Ventilator selected" : "Association completed";
  }

  patientRemoved() {
    this.patient = null;
    this.successMessage = "Patient removed";
  }

  ventRemoved() {
    this.ventilator = null;
    this.successMessage = "Ventilator removed";
  }

  async startNewAssociation() {
    try {
      if (this.scanType === ScanResponseType.DEVICE && this.scannedEntity.serial !== this.ventilator.serial) {
        this.resetWorkflow();
        this.scanFirst = this.scanResponseType.DEVICE;
        await this.handleVentilatorResponse(this.scannedEntity);
      } else if (this.scanType === ScanResponseType.PATIENT && this.scannedEntity?.id !== this.patient?.id) {
        this.resetWorkflow();
        this.scanFirst = this.scanResponseType.PATIENT;
        await this.handlePatientResponse(this.scannedEntity);
      }
    } finally {
      EventBus.$emit(FOCUS_SEARCHBAR);
    }
  }

  hideModal(modalName: string) {
    this.input = null;
    (this as any)[modalName] = false;

    EventBus.$emit(FOCUS_SEARCHBAR);
  }

  hideSuccessAlert() {
    this.successMessage = "";
    EventBus.$emit(FOCUS_SEARCHBAR);
  }

  resetWorkflow() {
    this.patient = null;
    this.ventilator = null;
    this.tempPatientID = null;
    this.tempDevice = null;
    this.successMessage = "";
    this.scanFirst = null;
    EventBus.$emit(FOCUS_SEARCHBAR);
  }
}
